// TUT-483 — o vocabulário da tela de Contas a Receber, e o espelho da lista
// para a demonstração (que roda sem servidor).
//
// A situação, o marco, a rota, a busca, os filtros e a ordem são a MESMA regra
// de `apps/api/src/domain/accounts-receivable-list.ts`. O web é pacote
// separado e não importa da API — a cópia é o preço do modo demonstração, e o
// `contas-a-receber.test.ts` prova os mesmos casos dos dois lados.
import type {
  AccountsReceivable,
  AccountsReceivableListQuery,
  AccountsReceivableListResponse,
  AccountsReceivableRoute,
  AccountsReceivableStage,
  AccountsReceivableStatus,
  FaixaDoVencimento,
  StatusTone,
} from "@/lib/types";
import { calendarDateKey } from "@/lib/format";

/**
 * Na ordem da esteira: aguardando → pronto → faturado → recebido → cancelado.
 * TUT-506: a faturada espera o dinheiro (informativo) e a recebida fecha
 * (sucesso) — "Recebido" é o PAGO do quadro do Financeiro.
 */
export const SITUACOES_DE_CONTA_A_RECEBER: readonly {
  chave: AccountsReceivableStatus;
  chaveDoSelo: string;
  rotulo: string;
  tom: StatusTone;
}[] = [
  { chave: "WAITING_REQUIREMENT", chaveDoSelo: "aguardando", rotulo: "Aguardando requisito", tom: "warning" },
  { chave: "READY_TO_INVOICE", chaveDoSelo: "pronto", rotulo: "Pronto para faturar", tom: "info" },
  { chave: "INVOICED", chaveDoSelo: "faturado", rotulo: "Faturado", tom: "info" },
  { chave: "RECEIVED", chaveDoSelo: "recebido", rotulo: "Recebido", tom: "success" },
  { chave: "CANCELLED", chaveDoSelo: "cancelado", rotulo: "Cancelado", tom: "neutral" },
];

/** TUT-506 — as encerradas: o dinheiro chegou, ou a cobrança deixou de existir. */
export function contaAReceberEncerrada(status: AccountsReceivableStatus): boolean {
  return status === "RECEIVED" || status === "CANCELLED";
}

/** TUT-506 — espelho de `FAIXAS_DO_VENCIMENTO` (API): a faturada pelo vencimento. */
export const FAIXAS_DO_VENCIMENTO: readonly { chave: FaixaDoVencimento; rotulo: string }[] = [
  { chave: "vencidas", rotulo: "Vencidas" },
  { chave: "a_vencer", rotulo: "A vencer" },
  { chave: "sem_vencimento", rotulo: "Sem vencimento" },
];

export function isFaixaDoVencimento(valor: unknown): valor is FaixaDoVencimento {
  return typeof valor === "string" && FAIXAS_DO_VENCIMENTO.some((item) => item.chave === valor);
}

/**
 * TUT-506 (revisão, M4) — HOJE no fuso do ESCRITÓRIO, como a API decide a
 * vencida (`escritorio.fuso`). O dia do navegador discordaria do servidor
 * perto da meia-noite para quem está fora do fuso.
 */
export function hojeNoEscritorio(agora: Date = new Date()): string {
  return calendarDateKey(agora);
}

/** O dia da fatura no fuso do escritório (ou hoje, para a conta que vai ser faturada agora). */
export function diaDaFatura(conta: Pick<AccountsReceivable, "invoicedAt">, hoje: string = hojeNoEscritorio()): string {
  return conta.invoicedAt ? calendarDateKey(conta.invoicedAt) : hoje;
}

/** `AAAA-MM-DD` deslocado em anos inteiros (29/02 vira 28/02 no ano comum). */
function somarAnos(dia: string, anos: number): string {
  const [ano = 0, mes = 1, dd = 1] = dia.split("-").map(Number);
  const ultimoDoMes = new Date(Date.UTC(ano + anos, mes, 0)).getUTCDate();
  return `${String(ano + anos).padStart(4, "0")}-${String(mes).padStart(2, "0")}-${String(Math.min(dd, ultimoDoMes)).padStart(2, "0")}`;
}

/** Espelho de `limitesDoVencimento` (API): de um ano antes do dia da fatura até cinco anos depois. */
export function limitesDoVencimento(diaDaFaturaDaConta: string): { min: string; max: string } {
  return { min: somarAnos(diaDaFaturaDaConta, -1), max: somarAnos(diaDaFaturaDaConta, 5) };
}

/**
 * TUT-506 — vencida: faturada, com vencimento antes de hoje. `hoje` é o dia
 * do escritório (`hojeNoEscritorio()`), o mesmo que a API usa.
 */
export function contaAReceberVencida(conta: Pick<AccountsReceivable, "status" | "dueDate">, hoje: string): boolean {
  return conta.status === "INVOICED" && conta.dueDate !== null && conta.dueDate < hoje;
}

/** A faixa da conta, ou `null` se ela não espera dinheiro (não está faturada). */
export function faixaDoVencimento(conta: Pick<AccountsReceivable, "status" | "dueDate">, hoje: string): FaixaDoVencimento | null {
  if (conta.status !== "INVOICED") return null;
  if (!conta.dueDate) return "sem_vencimento";
  return contaAReceberVencida(conta, hoje) ? "vencidas" : "a_vencer";
}

/** Os marcos que geram cobrança, na ordem do fluxo. */
export const MARCOS_DE_CONTA_A_RECEBER: readonly { chave: AccountsReceivableStage; rotulo: string }[] = [
  { chave: "QUESTIONS", rotulo: "Quesitos" },
  { chave: "ASSISTANT_NOMINATION", rotulo: "Indicação de assistentes" },
  { chave: "FIELD_COLLECTION", rotulo: "Levantamento" },
  { chave: "REPORT", rotulo: "Laudo" },
  { chave: "IMPUGNATION", rotulo: "Impugnação" },
  { chave: "REEXPERTISE", rotulo: "Re-perícia" },
];

export const ROTAS_DE_CONTA_A_RECEBER: readonly { chave: AccountsReceivableRoute; rotulo: string }[] = [
  { chave: "DIRECT", rotulo: "Direta" },
  { chave: "PURCHASE_ORDER", rotulo: "Pedido de compra" },
  { chave: "CLIENT_PLATFORM", rotulo: "Plataforma do cliente" },
];

const chavesDe = <T extends string>(itens: readonly { chave: T }[]): readonly string[] => itens.map((item) => item.chave);

export function isSituacaoDeContaAReceber(valor: unknown): valor is AccountsReceivableStatus {
  return typeof valor === "string" && chavesDe(SITUACOES_DE_CONTA_A_RECEBER).includes(valor);
}
export function isMarcoDeContaAReceber(valor: unknown): valor is AccountsReceivableStage {
  return typeof valor === "string" && chavesDe(MARCOS_DE_CONTA_A_RECEBER).includes(valor);
}
export function isRotaDeContaAReceber(valor: unknown): valor is AccountsReceivableRoute {
  return typeof valor === "string" && chavesDe(ROTAS_DE_CONTA_A_RECEBER).includes(valor);
}

export function situacaoDaConta(status: AccountsReceivableStatus) {
  const situacao = SITUACOES_DE_CONTA_A_RECEBER.find((item) => item.chave === status);
  return { key: situacao?.chaveDoSelo ?? status, label: situacao?.rotulo ?? status, tone: situacao?.tom ?? "neutral" } as const;
}

export function rotuloDoMarco(conta: Pick<AccountsReceivable, "stage" | "occurrence">): string {
  // TUT-585 — a conta avulsa não nasce de um marco do contrato.
  if (!conta.stage) return "—";
  const marco = MARCOS_DE_CONTA_A_RECEBER.find((item) => item.chave === conta.stage)?.rotulo ?? conta.stage;
  return conta.occurrence > 1 ? `${marco} ${conta.occurrence}` : marco;
}

export function rotuloDaRota(rota: AccountsReceivableRoute): string {
  return ROTAS_DE_CONTA_A_RECEBER.find((item) => item.chave === rota)?.rotulo ?? rota;
}

function normalizar(texto: string): string {
  return texto.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLocaleLowerCase("pt-BR");
}

function comparar(a: string | number | null, b: string | number | null, direcao: "asc" | "desc" | undefined): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const sinal = direcao === "desc" ? -1 : 1;
  if (typeof a === "number" && typeof b === "number") return sinal * (a - b);
  return sinal * String(a).localeCompare(String(b), "pt-BR", { numeric: true, sensitivity: "base" });
}

const posicao = (ordem: readonly string[], chave: string) => {
  const indice = ordem.indexOf(chave);
  return indice === -1 ? ordem.length : indice;
};

/** O espelho de `montarListaDeContasAReceber` (API), para a demonstração. */
export function montarListaDeContasAReceber(
  todas: readonly AccountsReceivable[],
  filtros: AccountsReceivableListQuery & { includeClosed?: boolean; hoje?: string },
): AccountsReceivableListResponse {
  const busca = filtros.search?.trim() ? normalizar(filtros.search.trim()) : null;
  const doProcesso = filtros.processo?.trim() ? normalizar(filtros.processo.trim()) : null;
  const situacoes = filtros.status?.length ? new Set<string>(filtros.status) : null;
  const clientes = filtros.cliente?.length ? new Set(filtros.cliente) : null;
  const marcos = filtros.marco?.length ? new Set<string>(filtros.marco) : null;
  const rotas = filtros.rota?.length ? new Set<string>(filtros.rota) : null;
  const faixas = filtros.vencimento?.length ? new Set<string>(filtros.vencimento) : null;
  const hoje = filtros.hoje ?? "";
  const comPeriodo = Boolean(filtros.de || filtros.ate);
  const recortadas = todas.filter((conta) => {
    if (situacoes) {
      if (!situacoes.has(conta.status)) return false;
    } else if (!filtros.includeClosed && contaAReceberEncerrada(conta.status)) {
      return false;
    }
    if (faixas) {
      const faixa = faixaDoVencimento(conta, hoje);
      if (!faixa || !faixas.has(faixa)) return false;
    }
    if (comPeriodo) {
      if (!conta.dueDate) return false;
      if (filtros.de && conta.dueDate < filtros.de) return false;
      if (filtros.ate && conta.dueDate > filtros.ate) return false;
    }
    return (
      (!busca || normalizar(conta.organizationName).includes(busca) || normalizar(conta.processNumber ?? "").includes(busca) || normalizar(conta.description ?? "").includes(busca)) &&
      (!doProcesso || normalizar(conta.processNumber ?? "").includes(doProcesso)) &&
      (!clientes || clientes.has(conta.organizationId)) &&
      (!marcos || (conta.stage !== null && marcos.has(conta.stage))) &&
      (!rotas || rotas.has(conta.route)) &&
      (filtros.valorMin === undefined || conta.amountCents >= filtros.valorMin) &&
      (filtros.valorMax === undefined || conta.amountCents <= filtros.valorMax)
    );
  });
  const valor: Record<string, (conta: AccountsReceivable) => string | number | null> = {
    cliente: (conta) => conta.organizationName,
    processo: (conta) => conta.processNumber,
    marco: (conta) => conta.stage ? posicao(chavesDe(MARCOS_DE_CONTA_A_RECEBER), conta.stage) * 1000 + conta.occurrence : null,
    rota: (conta) => posicao(chavesDe(ROTAS_DE_CONTA_A_RECEBER), conta.route),
    vencimento: (conta) => conta.dueDate,
    valor: (conta) => conta.amountCents,
    situacao: (conta) => posicao(chavesDe(SITUACOES_DE_CONTA_A_RECEBER), conta.status),
  };
  const acessor = filtros.sort ? valor[filtros.sort] : undefined;
  const ordenadas = [...recortadas].sort((a, b) => {
    const principal = acessor
      ? comparar(acessor(a), acessor(b), filtros.dir)
      : b.createdAt.localeCompare(a.createdAt);
    return principal || a.id.localeCompare(b.id, "pt-BR", { numeric: true });
  });
  const porId = new Map<string, string>();
  for (const conta of todas) if (!porId.has(conta.organizationId)) porId.set(conta.organizationId, conta.organizationName);
  const opcoes = [...porId]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR") || a.id.localeCompare(b.id));
  const total = ordenadas.length;
  if (!filtros.page || !filtros.pageSize) {
    return { items: ordenadas, page: 1, pageSize: total || 1, total, totalPages: 1, clientes: opcoes };
  }
  const totalPages = Math.max(1, Math.ceil(total / filtros.pageSize));
  const page = Math.min(Math.max(1, filtros.page), totalPages);
  return {
    items: ordenadas.slice((page - 1) * filtros.pageSize, page * filtros.pageSize),
    page,
    pageSize: filtros.pageSize,
    total,
    totalPages,
    clientes: opcoes,
  };
}
